import React, { useEffect, useState, useCallback } from 'react';
import { Box, Grid } from '@mui/material';

import {
  createChat,
  deleteChat,
  getChats,
  updateChat,
} from '../../actions/chat';
import { useUser } from '../../providers/UserContext';
import ChatComponent from './Chat';
import LoadingMask from '../shared/LoadingMask';
import ChatSelection from '../shared/ChatSelection';
import CHAT_TYPES from '../chatTypes';
import { useApi } from '../../providers/ApiContext';
import { usePageTitleDispatch } from '../../providers/PageTitleContext';
import { SET_PAGE_TITLE } from '../../providers/actionTypes';
import type { Chat, ChatUpdateRequest, EntityId } from '@/types';

const page_title = 'Voyager';
const PAGE_SIZE = 20;

interface ChatListResponse {
  results: Chat[];
  last_key: string | null;
}

function Voyager() {
  const api = useApi();
  const user = useUser();
  const [chats, setChats] = useState<Chat[] | null>(null);
  const [activeChat, setActiveChat] = useState<Chat | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [cursors, setCursors] = useState<Record<number, string>>({});

  const pageTitleDispatch = usePageTitleDispatch();

  useEffect(() => {
    pageTitleDispatch({ type: SET_PAGE_TITLE, payload: page_title });
  }, [pageTitleDispatch]);

  const fetchChats = useCallback(
    async (startKey: string | null = null) => {
      if (!api) return;
      setLoading(true);
      try {
        const response = (await getChats(
          api,
          CHAT_TYPES.VOYAGER,
          PAGE_SIZE,
          startKey || undefined,
        )) as unknown as ChatListResponse;
        setChats((prevChats) =>
          startKey === null
            ? response.results
            : [...(prevChats || []), ...response.results],
        );
        setHasNextPage(response.last_key !== null);
        if (response.last_key) {
          setCursors((prev) => ({
            ...prev,
            [Object.keys(prev).length + 1]: response.last_key!,
          }));
        }
        if (!activeChat && response.results.length > 0) {
          setActiveChat(response.results[0]);
        }
      } catch (error) {
        console.log(error);
        setChats([]);
      } finally {
        setLoading(false);
      }
    },
    [api, activeChat],
  );

  useEffect(() => {
    if (user?.entity_id && !chats) {
      setCursors({});
      fetchChats();
    }
  }, [user?.entity_id, chats, fetchChats]);

  useEffect(() => {
    if (user?.entity_id && chats && chats.length === 0 && api) {
      createChat(api, {
        name: 'New Chat',
        type: CHAT_TYPES.VOYAGER,
      })
        .then((chat) => {
          setChats([chat]);
          setActiveChat(chat);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [user?.entity_id, chats, api]);

  const handleUpdateChat = async (
    chat_id: EntityId,
    update: ChatUpdateRequest,
  ) => {
    if (!api) return;
    const updated_chat = await updateChat(api, chat_id, update);
    setChats((prevChats) => {
      if (!prevChats) return [updated_chat];
      const index = prevChats.findIndex((chat) => chat.entity_id === chat_id);
      const new_chats = [...prevChats];
      new_chats[index] = updated_chat;
      return new_chats;
    });
    setActiveChat(updated_chat);
  };

  const handleDeleteChat = async (chat_id: EntityId) => {
    if (!api) return;
    await deleteChat(api, chat_id);
    setChats((prevChats) => {
      if (!prevChats) return [];
      const updatedChats = prevChats.filter(
        (chat) => chat.entity_id !== chat_id,
      );

      // If we're deleting the active chat, select another one
      if (activeChat?.entity_id === chat_id) {
        if (updatedChats.length > 0) {
          // Select the first available chat, or the one that was before the deleted one
          const deletedChatIndex = prevChats.findIndex(
            (chat) => chat.entity_id === chat_id,
          );
          // Ensure the index is within bounds of the remaining chats
          const safeIndex = Math.min(deletedChatIndex, updatedChats.length - 1);
          const newActiveChat = updatedChats[safeIndex] || updatedChats[0];
          setActiveChat(newActiveChat);
        } else {
          // No chats left, set to null (will trigger creation of new chat)
          setActiveChat(null);
        }
      }

      return updatedChats;
    });
  };

  const handleLoadMore = useCallback(() => {
    if (!loading && hasNextPage) {
      const nextCursor = cursors[Object.keys(cursors).length];
      if (nextCursor) {
        fetchChats(nextCursor);
      }
    }
  }, [loading, hasNextPage, cursors, fetchChats]);

  if (!chats) {
    return <LoadingMask />;
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        marginTop: 0,
        paddingTop: 0,
      }}
    >
      <Grid container sx={{ height: '100%', flex: 1 }}>
        <Grid
          size={2}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              padding: '15px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <ChatSelection
              type={CHAT_TYPES.VOYAGER}
              new_name={'New Chat'}
              chats={chats}
              activeChat={activeChat}
              setActiveChat={setActiveChat}
              setChats={setChats as React.Dispatch<React.SetStateAction<Chat[]>>}
              onLoadMore={handleLoadMore}
              hasMore={hasNextPage}
              loading={loading}
            />
          </Box>
        </Grid>
        <Grid
          size={10}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ChatComponent
            key={activeChat?.entity_id}
            chat={activeChat || ({} as Chat)}
            updateChat={handleUpdateChat}
            deleteChat={handleDeleteChat}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default Voyager;

